import { useQuery } from '@apollo/client';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MY_CPM_CAMPAIGN_ACCOUNT_DISQUALIFICATIONS } from '@/queries/accountDisqualification';
import type { MyCpmCampaignAccountDisqualificationsQuery } from '@/generated/account-disqualification';

/** Lists the viewer's own disqualified accounts in a campaign; renders nothing when there are none. */
export function CampaignAccountDisqualificationNotice({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery<MyCpmCampaignAccountDisqualificationsQuery>(MY_CPM_CAMPAIGN_ACCOUNT_DISQUALIFICATIONS, {
    variables: { campaignId },
    skip: !campaignId,
  });
  const items = data?.myCpmCampaignAccountDisqualifications ?? [];
  if (items.length === 0) return null;
  const prefix = 'campaign.accountDisqualification';
  return (
    <div
      role="alert"
      data-testid="account-disqualification-notice"
      className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm"
    >
      <p className="flex items-center gap-2 font-semibold text-amber-600 dark:text-amber-400">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        {t(`${prefix}.title`)}
      </p>
      <p className="mt-1 text-muted-foreground">{t(`${prefix}.description`)}</p>
      <ul className="mt-2 space-y-1">
        {items.map(item => (
          <li key={item.id}>
            <span className="font-medium">
              {t(`${prefix}.item`, {
                account: item.account_label,
                platform: t(`campaign.channels.platformLabel.${item.platform}`, { defaultValue: item.platform }),
              })}
            </span>
            <span className="block whitespace-pre-wrap text-muted-foreground">{t(`${prefix}.reason`, { reason: item.reason })}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
